import { useMediaQuery } from "@mui/material";
import { useMemo } from "react";
import styles from "./style.module.scss";

export interface TrendSeries {
  key: string;
  label: string;
  color: string;
  /** One value per x label. */
  points: number[];
  /** Appended to axis ticks and tooltips, e.g. "%". */
  suffix?: string;
}

interface TrendChartProps {
  series: TrendSeries[];
  labels: string[];
  /** Number of horizontal grid lines / y ticks. */
  tickCount?: number;
}

const VIEW_W = 600;
const VIEW_H = 200;

/** Rounds an axis maximum up to a readable step. */
function niceMax(value: number): number {
  const step = value > 100 ? 50 : value > 20 ? 10 : value > 5 ? 2 : 1;
  return Math.max(step, Math.ceil(value / step) * step);
}

/**
 * Catmull-Rom to cubic bezier, so the line curves through every point
 * instead of past it. Same construction as the design file.
 */
function smoothPath(points: [number, number][]): string {
  if (!points.length) return "";

  let d = `M${points[0][0]},${points[0][1]}`;

  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;

    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];

    d += ` C${c1[0]},${c1[1]} ${c2[0]},${c2[1]} ${p2[0]},${p2[1]}`;
  }

  return d;
}

/** Multi-series area + line chart with y ticks, grid and hoverable dots. */
function TrendChart({ series, labels, tickCount = 6 }: TrendChartProps) {
  const count = labels.length;

  /* One label per bucket runs into its neighbour long before a phone gets a
     month of them. Thin the axis to a readable number and keep the ends. */
  const narrow = useMediaQuery("(max-width:600px)");
  const maxLabels = narrow ? 4 : 8;

  const model = useMemo(() => {
    const xAt = (index: number) =>
      count > 1 ? (index / (count - 1)) * VIEW_W : VIEW_W / 2;

    // The first series owns the axis; the rest are scaled to their own peak.
    const primary = series[0];
    const axisMax = primary
      ? niceMax(Math.max(...primary.points, 0) * 1.1 || 1)
      : 1;

    const paths = series.map((item, seriesIndex) => {
      const max =
        seriesIndex === 0
          ? axisMax
          : Math.max(...item.points, 0) * 1.1 || 1;

      const coords: [number, number][] = item.points.map((value, index) => [
        xAt(index),
        VIEW_H - (value / max) * VIEW_H,
      ]);

      const line = smoothPath(coords);

      return {
        ...item,
        line,
        area: `${line} L${VIEW_W},${VIEW_H} L0,${VIEW_H} Z`,
        dots: coords.map(([x, y], index) => ({
          left: `${(x / VIEW_W) * 100}%`,
          top: `${(y / VIEW_H) * 100}%`,
          tip: `${labels[index]}: ${item.points[index]}${item.suffix ?? ""} ${item.label}`,
        })),
      };
    });

    const ticks = Array.from({ length: tickCount }, (_, index) => ({
      label: `${Math.round(
        axisMax - (index * axisMax) / (tickCount - 1)
      )}${primary?.suffix ?? ""}`,
      top: `${(index / (tickCount - 1)) * 100}%`,
    }));

    // Evenly spaced picks that always keep the first and last bucket.
    const shown = Math.min(count, maxLabels);
    const stride = shown > 1 ? (count - 1) / (shown - 1) : 0;
    const visible = new Set(
      Array.from({ length: shown }, (_, index) => Math.round(index * stride))
    );

    const xLabels = labels
      .map((label, index) => ({
        label,
        index,
        left: `${(xAt(index) / VIEW_W) * 100}%`,
        shift:
          index === 0 ? "0" : index === count - 1 ? "-100%" : "-50%",
      }))
      .filter((tick) => visible.has(tick.index));

    return { paths, ticks, xLabels };
  }, [series, labels, count, tickCount, maxLabels]);

  return (
    <>
      <div className={styles.chartGrid}>
        <div className={styles.yAxis}>
          {model.ticks.map((tick) => (
            <span key={tick.label + tick.top} style={{ top: tick.top }}>
              {tick.label}
            </span>
          ))}
        </div>

        <div className={styles.plot}>
          <svg
            viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
            preserveAspectRatio="none"
            className={styles.svg}
          >
            <defs>
              {model.paths.map((item) => (
                <linearGradient
                  key={item.key}
                  id={`sa-trend-${item.key}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={item.color} stopOpacity="0.22" />
                  <stop offset="100%" stopColor={item.color} stopOpacity="0" />
                </linearGradient>
              ))}
            </defs>

            {model.paths.map((item) => (
              <g key={item.key}>
                <path d={item.area} fill={`url(#sa-trend-${item.key})`} />
                <path
                  d={item.line}
                  fill="none"
                  stroke={item.color}
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            ))}
          </svg>

          {model.paths.map((item) =>
            item.dots.map((dot, index) => (
              <div
                key={`${item.key}-${index}`}
                title={dot.tip}
                className={styles.dot}
                style={{
                  left: dot.left,
                  top: dot.top,
                  borderColor: item.color,
                }}
              />
            ))
          )}
        </div>
      </div>

      <div className={styles.chartGrid}>
        <div />
        <div className={styles.xAxis}>
          {model.xLabels.map((tick) => (
            <span
              key={tick.index}
              style={{ left: tick.left, transform: `translateX(${tick.shift})` }}
            >
              {tick.label}
            </span>
          ))}
        </div>
      </div>
    </>
  );
}

export default TrendChart;
